import { useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { AccountsPayableTab } from '../components/purchases/AccountsPayableTab'
import { ExpensesTab } from '../components/purchases/ExpensesTab'
import { PriceAdjustmentsTab } from '../components/purchases/PriceAdjustmentsTab'
import { ProductEntriesTab } from '../components/purchases/ProductEntriesTab'
import { SuggestedOrdersTab } from '../components/purchases/SuggestedOrdersTab'
import { SuppliersTab } from '../components/purchases/SuppliersTab'
import { ManualEntryModal } from '../components/purchases/modals/ManualEntryModal'
import { PriceAdjustmentModal } from '../components/purchases/modals/PriceAdjustmentModal'
import { XmlEntryWizardModal } from '../components/purchases/modals/XmlEntryWizardModal'
import { ManualEntryFormValues } from '../components/purchases/modals/manual-entry-form'
import { buildExcelPurchaseItemPayload, buildManualPurchaseItemPayload } from '../components/purchases/modals/manual-entry-payload'
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs'
import {
  ManualPurchasePayload,
  ExcelPurchasePayload,
  PriceAdjustment,
  purchasesService,
} from '../services/purchases.service'
import { useSessionStore } from '../store/auth/auth.store'

export function Purchases() {
  const queryClient = useQueryClient()
  const canAction = useSessionStore((state) => state.canAction)
  const canViewPurchases = canAction('purchases.purchase.view')
  const canViewSuppliers = canAction('purchases.supplier.view')
  const canViewAccountsPayable = canAction('purchases.account_payable.view')
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showManualEntryModal, setShowManualEntryModal] = useState(false)
  const [showPriceAdjustmentModal, setShowPriceAdjustmentModal] = useState(false)
  const [productEntriesRefreshKey, setProductEntriesRefreshKey] = useState(0)
  const [selectedAdjustment, setSelectedAdjustment] = useState<PriceAdjustment | null>(null)
  const [activeTab, setActiveTab] = useState(
    canViewPurchases ? 'suggested' : canViewAccountsPayable ? 'accounts-payable' : 'suppliers',
  )

  const refreshPurchases = () => {
    setProductEntriesRefreshKey((value) => value + 1)
    void queryClient.invalidateQueries({ queryKey: ['purchase-suggested-orders'] })
    void queryClient.invalidateQueries({ queryKey: ['purchase-accounts-payable'] })
  }

  useEffect(() => {
    async function loadPrototypeData() {
      try {
        // Esta carga compartida se conserva hasta que los otros prototipos sean reemplazados.
        await purchasesService.getSuppliers()
      } catch (error) {
        console.error('Error loading purchase prototypes:', error)
      }
    }
    void loadPrototypeData()
  }, [])

  const handleManualEntrySubmit = async (
    data: ManualEntryFormValues,
    context: { excelFile?: File; destination: { id: number; name: string } },
  ) => {
    const { destination, excelFile } = context
    const header = {
      sucursal_id: Number(data.sucursalId),
      reference_number: data.noteNumber.trim(),
      supplier_id: Number(data.supplierId),
      purchase_date: data.purchaseDate,
      entry_date: data.entryDate,
      payment_condition: data.paymentCondition,
      payment_method_id: Number(data.paymentMethodId),
    }
    try {
      if (excelFile) {
        const excelPayload: ExcelPurchasePayload = {
          ...header,
          items: data.products.map(buildExcelPurchaseItemPayload),
        }
        await purchasesService.createExcelPurchase(excelPayload, excelFile)
        toast.success(`Ingreso desde Excel registrado en ${destination.name}.`)
      } else {
        const manualPayload: ManualPurchasePayload = {
          ...header,
          items: data.products.map(buildManualPurchaseItemPayload),
        }
        await purchasesService.createManualPurchase(manualPayload)
        toast.success(`Ingreso manual registrado en ${destination.name}.`)
      }
      setShowManualEntryModal(false)
      refreshPurchases()
    } catch (error: unknown) {
      const message = isAxiosError<{ message?: string }>(error)
        ? error.response?.data?.message
        : undefined
      toast.error(message || 'No fue posible registrar el ingreso')
    }
  }

  const openPriceAdjustmentModal = (adjustment: PriceAdjustment) => {
    setSelectedAdjustment(adjustment)
    setShowPriceAdjustmentModal(true)
  }

  return (
    <div className="space-y-6 p-6">
      <h1 className="text-2xl font-semibold text-header-foreground">Compras y Proveedores</h1>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="h-auto max-w-full flex-wrap justify-start gap-1">
          {canViewPurchases && <TabsTrigger value="suggested">Compras sugeridas</TabsTrigger>}
          {canViewPurchases && <TabsTrigger value="products">Compras</TabsTrigger>}
          {canViewAccountsPayable && <TabsTrigger value="accounts-payable">Cuentas por pagar</TabsTrigger>}
          <TabsTrigger value="expenses">Gestión de gastos</TabsTrigger>
          {canViewPurchases && <TabsTrigger value="adjustments">Ajustes de Precio</TabsTrigger>}
          {canViewSuppliers && <TabsTrigger value="suppliers">Proveedores</TabsTrigger>}
        </TabsList>

        {canViewPurchases && <SuggestedOrdersTab active={activeTab === 'suggested'} />}
        {canViewPurchases && (
          <ProductEntriesTab
            refreshKey={productEntriesRefreshKey}
            openXmlEntry={() => setShowUploadModal(true)}
            openManualEntry={() => setShowManualEntryModal(true)}
          />
        )}
        {canViewAccountsPayable && <AccountsPayableTab active={activeTab === 'accounts-payable'} />}
        <ExpensesTab />
        {canViewPurchases && (
          <PriceAdjustmentsTab
            openPriceAdjustmentModal={openPriceAdjustmentModal}
            refreshKey={productEntriesRefreshKey}
          />
        )}
        {canViewSuppliers && <SuppliersTab />}
      </Tabs>

      <XmlEntryWizardModal
        open={showUploadModal}
        onOpenChange={setShowUploadModal}
        onCreated={refreshPurchases}
      />
      <ManualEntryModal
        showManualEntryModal={showManualEntryModal}
        setShowManualEntryModal={setShowManualEntryModal}
        onConfirm={handleManualEntrySubmit}
      />
      <PriceAdjustmentModal
        showPriceAdjustmentModal={showPriceAdjustmentModal}
        setShowPriceAdjustmentModal={setShowPriceAdjustmentModal}
        selectedAdjustment={selectedAdjustment}
      />
    </div>
  )
}
